// Avancement d'une commande et reste vendable d'un camion: port exact de
// backend/app/core/order_progress.py et backend/app/core/trip_remaining.py.
// Les deux implémentations sont vérifiées par les mêmes jeux de données
// (backend/app/tests/fixtures/offline_parity/*.json).

export const SEQUENCE_STATUTS_TRAJET = [
  'CREATED',
  'EN_ROUTE_TO_BORDER',
  'AT_CUSTOMS_EXIT',
  'CLEARED_CUSTOMS_EXIT',
  'EN_ROUTE_TO_LOADING',
  'LOADING',
  'EN_ROUTE_TO_ENTRY_CUSTOMS',
  'AT_CUSTOMS_ENTRY',
  'CLEARED_CUSTOMS_ENTRY',
  'ARRIVED_AT_DEPOT',
  'EN_ROUTE_TO_DELIVERY',
  'DELIVERED',
  'COMPLETED',
];

const ETATS = {
  BROUILLON: 'Brouillon',
  A_AFFECTER: 'À affecter',
  PARTIELLE: 'Affectée en partie',
  AFFECTEE: 'Affectée',
  EN_ROUTE: 'En route',
  LIVREE: 'Livrée',
  ANNULEE: 'Annulée',
};

export const STATUTS_AFFECTABLES = new Set([
  'DRAFT', 'PENDING', 'CONFIRMED', 'ALLOCATED', 'IN_TRANSIT', 'PARTIALLY_DELIVERED',
]);
const STATUTS_CLOS = new Set(['DELIVERED', 'INVOICED', 'PAID']);
const INDEX_CHARGEMENT = SEQUENCE_STATUTS_TRAJET.indexOf('LOADING');
const STATUTS_PARTIS = new Set(SEQUENCE_STATUTS_TRAJET.slice(INDEX_CHARGEMENT));

const num = (valeur) => {
  const n = Number.parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
};

// Arrondi au millilitre, comme `round(x, 3)` côté serveur.
const litres = (valeur) => Math.round((valeur + Number.EPSILON) * 1000) / 1000;

const plaque = (trajet) => {
  if (!trajet) return null;
  for (const cle of ['plate', 'tractor_plate', 'truck_matricule', 'matricule']) {
    const valeur = trajet[cle];
    if (valeur && String(valeur).trim()) return String(valeur).trim();
  }
  return null;
};

const indexerTrajets = (trajets) => {
  if (!trajets) return {};
  if (!Array.isArray(trajets)) {
    return Object.fromEntries(Object.entries(trajets).map(([cle, valeur]) => [String(cle), valeur]));
  }
  return Object.fromEntries(trajets.filter((t) => t && t.id).map((t) => [String(t.id), t]));
};

function lignes(commande, affectations) {
  const rows = (affectations || []).filter((ligne) => ligne && typeof ligne === 'object');
  if (rows.length) {
    return {
      lignes: rows
        .filter((ligne) => !ligne.released_at)
        .map((ligne) => ({
          trip_id: ligne.trip_id,
          allocated: num(ligne.quantity_liters),
          delivered: num(ligne.delivered_quantity),
        })),
      source: 'REGISTRE',
    };
  }
  const cache = (commande.allocated_trips || []).filter((e) => e && typeof e === 'object');
  return {
    lignes: cache.map((e) => ({ trip_id: e.trip_id, allocated: num(e.quantity), delivered: 0 })),
    source: cache.length ? 'CACHE' : 'AUCUNE',
  };
}

export function calculerProgressionCommande(commande, affectations = null, trajets = null) {
  const statut = String(commande.status || '').toUpperCase();
  const commande_ = num(commande.quantity_liters);
  const annulee = statut === 'CANCELLED';
  const close = Boolean(commande.delivery_closed_at) || STATUTS_CLOS.has(statut);

  const { lignes: lgs, source } = lignes(commande, affectations);
  const affecte = lgs.reduce((somme, l) => somme + l.allocated, 0);
  const livre = Math.max(num(commande.delivered_quantity), lgs.reduce((somme, l) => somme + l.delivered, 0));

  const engage = Math.max(affecte, livre);
  const aAffecter = annulee || close ? 0 : Math.max(0, commande_ - engage);
  const aLivrer = annulee || close ? 0 : Math.max(0, commande_ - livre);

  const parId = indexerTrajets(trajets);
  let charge = 0;
  let partis = 0;
  let nonMesures = 0;
  const camions = [];
  const vus = new Set();
  for (const ligne of lgs) {
    const tripId = ligne.trip_id ? String(ligne.trip_id) : null;
    const trajet = tripId ? parId[tripId] : null;
    const statutTrajet = String(trajet?.status || '').toUpperCase();
    const estParti = STATUTS_PARTIS.has(statutTrajet);
    if (estParti) {
      partis += 1;
      const mesure = num(trajet?.fuel_quantity_real);
      if (mesure > 0) charge += Math.min(ligne.allocated, mesure);
      else nonMesures += 1;
    }
    if (tripId && vus.has(tripId)) continue;
    if (tripId) vus.add(tripId);
    camions.push({
      trip_id: tripId,
      plate: plaque(trajet),
      allocated_liters: litres(ligne.allocated),
      delivered_liters: litres(ligne.delivered),
      has_left: estParti,
    });
  }

  const commence = partis > 0 || livre > 0 || ['IN_TRANSIT', 'PARTIALLY_DELIVERED'].includes(statut);

  let etat;
  if (annulee) etat = 'ANNULEE';
  else if (close || (commande_ > 0 && livre >= commande_)) etat = 'LIVREE';
  else if (engage <= 0) etat = statut === 'DRAFT' ? 'BROUILLON' : 'A_AFFECTER';
  else if (aAffecter > 0) etat = 'PARTIELLE';
  else if (commence) etat = 'EN_ROUTE';
  else etat = 'AFFECTEE';

  return {
    ordered_liters: litres(commande_),
    allocated_liters: litres(affecte),
    loaded_liters: litres(charge),
    loaded_is_measured: partis > 0 && nonMesures === 0,
    delivered_liters: litres(livre),
    to_allocate_liters: litres(aAffecter),
    to_deliver_liters: litres(aLivrer),
    state: etat,
    state_label: ETATS[etat],
    truck_count: camions.length,
    departed_truck_count: partis,
    trucks: camions,
    can_allocate: Boolean(
      !annulee && !close && (!statut || STATUTS_AFFECTABLES.has(statut)) && aAffecter > 0,
    ),
    source,
  };
}

// --- Reste vendable d'un camion (trip_remaining.py) ------------------------------------------

export function volumeDeReference(trajet, camion = null) {
  const mesure = num(trajet.fuel_quantity_real);
  if (mesure > 0) return [mesure, true];
  return [num(camion?.capacity_liters), false];
}

export function volumeAffecte(affectations) {
  return (affectations || []).reduce(
    (somme, a) => (a.released_at ? somme : somme + num(a.quantity_liters)),
    0,
  );
}

export function calculerResteTrajet(trajet, affectations, camion = null) {
  const [reference] = volumeDeReference(trajet, camion);
  return Math.max(0, litres(reference - volumeAffecte(affectations)));
}

/**
 * Port de `refreshed_remaining` (trip_remaining.py): le cache `remaining_liters`
 * à écrire quand le volume chargé (ou le camion) change, ou null quand il n'y a
 * rien à écrire (jamais calculé et non mesuré: la répartition lit alors la
 * contenance du camion, et l'écrire à 0 ferait disparaître le camion).
 */
export function calculerResteApresChangementDeVolume(trajet, affectations, camion = null) {
  const [, mesure] = volumeDeReference(trajet, camion);
  const cache = trajet.remaining_liters;
  if ((cache === null || cache === undefined) && !mesure) return null;
  return calculerResteTrajet(trajet, affectations, camion);
}
